/**
 * useNavigationWarning Hook
 * 
 * Warns users when they try to navigate away with unsaved changes.
 * Uses browser's beforeunload event for browser navigation (refresh, close tab, external links).
 * 
 * Note: React Router's useBlocker requires a data router (createBrowserRouter).
 * Since this app uses BrowserRouter, we only handle beforeunload events.
 * For in-app navigation warnings, components should check hasUnsavedChanges before navigating.
 */

import { useEffect, useState } from 'react';

export interface UseNavigationWarningProps {
  /** Whether there are unsaved changes to warn about */
  hasUnsavedChanges: boolean;
  /** Custom message to show (defaults to standard browser message for beforeunload) */
  message?: string;
}

export function useNavigationWarning({
  hasUnsavedChanges,
  message = 'You have unsaved changes. Are you sure you want to leave?',
}: UseNavigationWarningProps) {
  // Track if a navigation warning dialog should be shown
  // This can be used by components to show a custom dialog before navigating
  const [showWarning, setShowWarning] = useState(false);
  
  // Handle browser navigation (refresh, close tab, external navigation)
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Standard way to trigger the browser's default dialog
        event.preventDefault();
        // Some browsers require returnValue to be set
        event.returnValue = message;
        return message;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message]);

  // Return state for component to handle confirmation
  // Note: Without data router, we can't block React Router navigation
  // Components should check hasUnsavedChanges before programmatic navigation
  return {
    /** Whether navigation warning should be shown (for manual dialog control) */
    isBlocked: showWarning,
    /** Show the warning dialog */
    showWarningDialog: () => setShowWarning(true),
    /** Hide the warning dialog and proceed */
    proceed: () => setShowWarning(false),
    /** Hide the warning dialog (user chose to stay) */
    reset: () => setShowWarning(false),
    /** Whether there are unsaved changes */
    hasUnsavedChanges,
  };
}

export default useNavigationWarning;

